/**
 * MockExamCTA — conversion block rendered near the top of every blog post.
 *
 * Why: blog posts are the site's main organic entry point, but until now the
 * only product link was the RelatedResources block at the very end of the
 * article. Readers who don't finish the post never see a way into the free
 * mock exam. This card puts the transactional offer (full mock exam, no
 * sign-up) in the first viewport after the header.
 *
 * Labels go through t() with English defaults: the blog is English except for the
 * posts in src/data/germanBlog.js, whose German strings live in de.js (blog.cta.*).
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Timer, ClipboardCheck } from 'lucide-react';
import { useLangParam } from '../../hooks/useLocalizedNavigate';
import { MOCK_EXAM_CATALOG } from '../../data/mockExamCatalog';

const CATALOG_BY_SLUG = new Map(MOCK_EXAM_CATALOG.map((e) => [e.slug, e]));

/** 'telc-b2-redemittel-schreiben' → catalog entry for 'telc-b2', else null. */
export function examFromPostSlug(postSlug) {
  const m = /^(telc|goethe|osd)-(b1|b2|c1)/.exec(postSlug || '');
  return m ? CATALOG_BY_SLUG.get(`${m[1]}-${m[2]}`) || null : null;
}

const MockExamCTA = ({ postSlug }) => {
  const lang = useLangParam();
  const { t } = useTranslation();
  const exam = examFromPostSlug(postSlug);
  const examLabel = exam ? exam.seoName : t('blog.cta.german', { defaultValue: 'German' });

  return (
    <aside
      aria-label={t('blog.cta.aria', { defaultValue: 'Free mock exam' })}
      className="my-8 rounded-2xl border-2 border-black bg-gradient-to-b from-black to-[#1a1a1a] text-white p-6 sm:p-7 shadow-lg"
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-5">
        <div className="flex-1">
          <p className="font-space text-xs font-black tracking-wider uppercase text-[#3A86FF] mb-2">
            {t('blog.cta.eyebrow', { defaultValue: 'Put it into practice' })}
          </p>
          <h2 className="font-space text-xl sm:text-2xl font-bold leading-tight mb-2">
            {t('blog.cta.heading', { exam: examLabel, defaultValue: 'Try a free {{exam}} mock exam' })}
          </h2>
          <p className="text-white/70 text-sm sm:text-base">
            {t('blog.cta.body', { defaultValue: 'Real exam format, real timing, instant results. No sign-up needed.' })}
          </p>
          <div className="flex flex-wrap gap-4 mt-3 text-white/60 text-xs sm:text-sm">
            <span className="inline-flex items-center gap-1.5">
              <Timer className="w-4 h-4 text-[#3A86FF]" aria-hidden="true" />
              {t('blog.cta.timing', { defaultValue: 'Official timing' })}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <ClipboardCheck className="w-4 h-4 text-[#3A86FF]" aria-hidden="true" />
              {t('blog.cta.sections', { defaultValue: 'All sections included' })}
            </span>
          </div>
        </div>
        <div className="flex flex-col gap-2 sm:min-w-[220px]">
          <Link
            to={`/${lang}/try`}
            className="group inline-flex items-center justify-center gap-2 min-h-[48px] px-6 py-3 bg-white text-black font-space font-bold rounded-xl transition-all duration-300 hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] touch-manipulation"
          >
            {t('blog.cta.start', { defaultValue: 'Start the free mock exam' })}
            <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
          </Link>
          {exam && (
            <Link
              to={`/${lang}/mock-exams/${exam.slug}`}
              className="inline-flex items-center justify-center min-h-[44px] px-4 py-2 text-white/80 hover:text-white font-space font-bold text-sm rounded-xl border border-white/20 hover:border-white/50 transition-all duration-300 active:scale-[0.98] touch-manipulation"
            >
              {t('blog.cta.format', { exam: examLabel, defaultValue: 'See the {{exam}} exam format' })}
            </Link>
          )}
        </div>
      </div>
    </aside>
  );
};

export default MockExamCTA;
